import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import {useTranslation} from 'react-i18next';
import {SMCCheckbox} from '../../../components/Simple/SMCCheckbox';
import SMCDayPickerInput from '../../../components/Simple/SMCDayPickerInput';
import FilterLayout from '../FilterLayout';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCExpansionPanel from '../../../components/Simple/SMCExpansionPanel';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {GatewayFilterSchema} from '../../../api/DBSchema/Gateway';
import { MenuItem, Select} from '@mui/material';
// import {Select} from '@mui/material';
// import {Checkbox} from "@chkp/ci-front-common-core";
import {makeStyles} from '@mui/styles'
// import {makeStyles} from 'tss-react/mui'
import SMCLabel from '../../../components/Simple/SMCLabel';
import {addMultipleItemsToArrayNoDuplicates, arrayContainsAll, removeMultipleItemsFromArray} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useBoolean} from '../../../utils/hooks';
import SetMacFileModal from '../../PopupPages/SetMacFileModal';
import {FilterTypes, ObjectTypes} from '../../../api/consts';
import {PlanGetListSchema} from '../../../api/DBSchema/Plan';
import SMCMenuItem from '../../../components/Simple/SMCMenuItem/index';


function DateRangeChooser({
    fromDate,
    toDate,
    setFromDate,
    setToDate
}) {
    return (
        <>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>From:</span>
            <SMCDayPickerInput
                placeholder="From..."
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setFromDate(null)} />
        </RowLayout>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>To:</span>
            <SMCDayPickerInput
                placeholder="To..."
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setToDate(null)} />
        </RowLayout>
        </>
    );
}

function GatewayFilterPane({
    initialFilter,
    onFilterChange,
    open
}) {
    const classes = useStyles();
    const { t } = useTranslation();
    const didMountRef = useRef(true);
    const freeTextSearchRef = useRef();

    const [isSetMacFolderModalOpen, openSetMacFolderModal, closeSetMacFolderModal] = useBoolean(false);

    useEffect(() => {
        if (open && freeTextSearchRef.current) {
            freeTextSearchRef.current.focus();
        }
    }, [open]);

    const [freeTextSearch, setFreeTextSearch] = useState(
        initialFilter.freeTextSearch ? initialFilter.freeTextSearch.value : ''
    );
        
    const [connected, setConnected] = useState(
        initialFilter.connected ? initialFilter.connected.value : true
    );
    const [disconnected, setDisconnected] = useState(
        initialFilter.disconnected ? initialFilter.disconnected.value : true
    );
    const [disabled, setDisabled] = useState(
        initialFilter.disabled ? initialFilter.disabled.value : true
    );

    const [macAddress, setMacAddress] = useState(
        initialFilter.mac ? initialFilter.mac.value : ''
    );
    const [macAddressList, setMacAddressList] = useState(
        initialFilter.macAddressList ? initialFilter.macAddressList.value : null
    );

    const [gatewayTypesList, setGatewayTypesList] = useState(
        initialFilter.gatewayType ? initialFilter.gatewayType.value : []
    );

    const [plan, setPlan] = useState(
        initialFilter.plan ? initialFilter.plan.value : ''
    );

    const [firmwaresList, setFirmwaresList] = useState(
        initialFilter.firmware ? initialFilter.firmware.value : []
    );
    
    const [vendor, setVendor] = useState(
        initialFilter.vendor ? initialFilter.vendor.value : ''
    );

    const [firstConnectionFromDate, setFirstConnectionFromDate] = useState(null);
    const [firstConnectionToDate, setFirstConnectionToDate] = useState(null);
    
    const [lastConnectionFromDate, setLastConnectionFromDate] = useState(null);
    const [lastConnectionToDate, setLastConnectionToDate] = useState(null);
    
    const [creationFromDate, setCreationFromDate] = useState(null);
    const [creationToDate, setCreationToDate] = useState(null);

    const [modificationFromDate, setModificationFromDate] = useState(null);
    const [modificationToDate, setModificationToDate] = useState(null);
    
    const [gatewayUsedFirmwares, setGatewayUsedFirmwares] = useState(null);
    const [planList, setPlanList] = useState(null);

    const resetToDefaults = useCallback(() => {
        setFreeTextSearch('');
        setConnected(true);
        setDisconnected(true);
        setDisabled(true);

        setMacAddress('');
        setMacAddressList(null);

        setVendor('');
        setGatewayTypesList([]);

        setPlan('');

        setFirmwaresList([]);

        setFirstConnectionFromDate(null);
        setFirstConnectionToDate(null);
        
        setLastConnectionFromDate(null);
        setLastConnectionToDate(null);

        setCreationFromDate(null);
        setCreationToDate(null);
        
        setModificationFromDate(null);
        setModificationToDate(null);
    }, []);


    useEffect(() => {
        // Don't run on first render
        if (didMountRef.current === true) {
            didMountRef.current = false;
            return;
        }

        const filterRequestObj = {};
        // Free text search
        if (freeTextSearch && freeTextSearch.length) {
            filterRequestObj[GatewayFilterSchema.freeTextSearch] = { value: freeTextSearch };
        }
        // Status
        filterRequestObj[GatewayFilterSchema.connected] = { value: connected };
        filterRequestObj[GatewayFilterSchema.disconnected] = { value: disconnected };
        filterRequestObj[GatewayFilterSchema.disabled] = { value: disabled };
        // Mac address
        if (macAddressList) {
            filterRequestObj[GatewayFilterSchema.macAddressList] = { value: macAddressList };
        } else if (macAddress) {
            filterRequestObj[GatewayFilterSchema.mac] = {
                value: macAddress,
                type: FilterTypes.contains
            };
        }
        // Vendor
        if (vendor) {
            filterRequestObj[GatewayFilterSchema.vendor] = { value: vendor };
        }
        // Gateway types
        if (gatewayTypesList && gatewayTypesList.length) {
            filterRequestObj[GatewayFilterSchema.gatewayType] = { value: gatewayTypesList };
        }
        // Plan
        if (plan) {
            filterRequestObj[GatewayFilterSchema.plan] = { value: plan };
        }
        // Firmwares
        if (firmwaresList && firmwaresList.length) {
            filterRequestObj[GatewayFilterSchema.reportedFirmware] = { value: firmwaresList };
        }
        // First connection date range
        if (firstConnectionFromDate || firstConnectionToDate) {
            filterRequestObj[GatewayFilterSchema.firstConnection] = {
                value: {
                    from: firstConnectionFromDate ? firstConnectionFromDate.getTime() : null,
                    to: firstConnectionToDate ? firstConnectionToDate.getTime() : null
                }
            };
        }
        // Last connection date range
        if (lastConnectionFromDate || lastConnectionToDate) {
            filterRequestObj[GatewayFilterSchema.lastConnection] = {
                value: {
                    from: lastConnectionFromDate ? lastConnectionFromDate.getTime() : null,
                    to: lastConnectionToDate ? lastConnectionToDate.getTime() : null
                }
            };
        }
        // Creation date range
        if (creationFromDate || creationToDate) {
            filterRequestObj[GatewayFilterSchema.creationDate] = {
                value: {
                    from: creationFromDate ? creationFromDate.getTime() : null,
                    to: creationToDate ? creationToDate.getTime() : null
                }
            };
        }
        // Modification date range
        if (modificationFromDate || modificationToDate) {
            filterRequestObj[GatewayFilterSchema.modificationDate] = {
                value: {
                    from: modificationFromDate ? modificationFromDate.getTime() : null,
                    to: modificationToDate ? modificationToDate.getTime() : null
                }
            };
        }
        
        onFilterChange(filterRequestObj);
    }, [
        freeTextSearch, onFilterChange,
        connected, disconnected, disabled,
        macAddress, macAddressList,
        gatewayTypesList, vendor, plan, firmwaresList,
        firstConnectionFromDate, firstConnectionToDate,
        lastConnectionFromDate, lastConnectionToDate,
        creationFromDate, creationToDate,
        modificationFromDate, modificationToDate
    ]);

    // Get gateways used firmwares on load
    useEffect(() => {
        Api.gatewayGetUsedFirmwaresList()
            .then(resp => {
                setGatewayUsedFirmwares(resp.data.firmwares);
            })
            .catch(e => {
                console.error('GatewayFilterPane: error reading gateway used firmwares', e);
            });
        Api.fetchObjectList(ObjectTypes.PLAN)
            .then(resp => {
                setPlanList(resp.data.data.map(_plan => _plan[PlanGetListSchema.name]));
            })
            .catch(e => {
                console.error('GatewayFilterPane: error reading plan list', e);
            });
    }, []);

    const gatewayFamilies = React.useMemo(() => ({
        family600: [ '600 Appliance' ],
        family1200: [ '1200R Appliance' ],
        family7001400: [ '730 Appliance', '750 Appliance', '790 Appliance', '1430 Appliance', '1450 Appliance', '1470 Appliance', '1490 Appliance' ],
        family1500: [ '1500 Appliance', '1530 Appliance', '1550 Appliance', '1570 Appliance', '1590 Appliance', '1570R Appliance' ],
        family16001800: [ '1600 Appliance', '1800 Appliance' ]
    }), []);

    const is600FamilySelected = useMemo(() => {
        return arrayContainsAll(gatewayTypesList, gatewayFamilies.family600);
    }, [gatewayTypesList, gatewayFamilies]);
    
    const is1200FamilySelected = useMemo(() => {
        return arrayContainsAll(gatewayTypesList, gatewayFamilies.family1200);
    }, [gatewayTypesList, gatewayFamilies]);

    const is7001400FamilySelected = useMemo(() => {
        return arrayContainsAll(gatewayTypesList, gatewayFamilies.family7001400);
    }, [gatewayTypesList, gatewayFamilies]);

    const is1500FamilySelected = useMemo(() => {
        return arrayContainsAll(gatewayTypesList, gatewayFamilies.family1500);
    }, [gatewayTypesList, gatewayFamilies]);

    const is16001800FamilySelected = useMemo(() => {
        return arrayContainsAll(gatewayTypesList, gatewayFamilies.family16001800);
    }, [gatewayTypesList, gatewayFamilies]);

    const gatewayTypesOnChange = useCallback((e, child) => {
        const selected = child.props.id;
        const selectedArray = e.target.value.filter(type => !!type); // Remove empty values (families have empty values)
        if (selected === '600 Family') {
            if (is600FamilySelected) {
                setGatewayTypesList(removeMultipleItemsFromArray(selectedArray, gatewayFamilies.family600));
            } else {
                setGatewayTypesList(addMultipleItemsToArrayNoDuplicates(selectedArray, gatewayFamilies.family600));
            }
        } else if (selected === '1200 Family') {
            if (is1200FamilySelected) {
                setGatewayTypesList(removeMultipleItemsFromArray(selectedArray, gatewayFamilies.family1200));
            } else {
                setGatewayTypesList(addMultipleItemsToArrayNoDuplicates(selectedArray, gatewayFamilies.family1200));
            }
        } else if (selected === '700/1400 Family') {
            if (is7001400FamilySelected) {
                setGatewayTypesList(removeMultipleItemsFromArray(selectedArray, gatewayFamilies.family7001400));
            } else {
                setGatewayTypesList(addMultipleItemsToArrayNoDuplicates(selectedArray, gatewayFamilies.family7001400));
            }
        } else if (selected === '1500 Family') {
            if (is1500FamilySelected) {
                setGatewayTypesList(removeMultipleItemsFromArray(selectedArray, gatewayFamilies.family1500));
            } else {
                setGatewayTypesList(addMultipleItemsToArrayNoDuplicates(selectedArray, gatewayFamilies.family1500));
            }
        } else if (selected === '1600/1800 Family') {
            if (is16001800FamilySelected) {
                setGatewayTypesList(removeMultipleItemsFromArray(selectedArray, gatewayFamilies.family16001800));
            } else {
                setGatewayTypesList(addMultipleItemsToArrayNoDuplicates(selectedArray, gatewayFamilies.family16001800));
            }
        } else {
            setGatewayTypesList(selectedArray)
        }
    }, [gatewayFamilies, is600FamilySelected, is1200FamilySelected, is7001400FamilySelected, is1500FamilySelected, is16001800FamilySelected]);

    return (
        <FilterLayout clearAllAction={resetToDefaults}>
            {/* Status tab */}
            <SMCExpansionPanel title={t('SMC_GATEWAY_STATUS')}>
                <SMCCheckbox
                    checked={connected}
                    onChange={e => setConnected(e.target.checked)}
                    label={
                        <RowLayout spacing="3px">
                            <SMCIcon icon="connected" alt="" style={{ verticalAlign: 'middle' }} />{t('SMC_GATEWAY_STATUS_CONNECTED')}
                        </RowLayout>
                    }
                />
                <SMCCheckbox
                    checked={disconnected}
                    onChange={e => setDisconnected(e.target.checked)}
                    label={
                        <RowLayout spacing="3px">
                            <SMCIcon icon="disconnected" alt="" style={{ verticalAlign: 'middle' }} />{t('SMC_GATEWAY_STATUS_DISCONNECTED')}
                        </RowLayout>
                    }
                />                    
                <SMCCheckbox
                    checked={disabled}
                    onChange={e => setDisabled(e.target.checked)}
                    label={
                        <RowLayout spacing="3px">
                            <SMCIcon icon="disabled" alt="" style={{ verticalAlign: 'middle' }} />{t('SMC_GATEWAY_STATUS_DISABLED_EXTERNAL')}
                        </RowLayout>
                    }
                />
            </SMCExpansionPanel>
            {/* MAC address tab */}
            <SMCExpansionPanel title={t('SMC_GATEWAY_MAC')}>
                <RowLayout>
                    <SMCTextInput value={macAddress} onChange={e => setMacAddress(e.target.value)} width={150} />
                    <FontAwesomeIcon icon="folder-open" onClick={openSetMacFolderModal} />
                    { macAddressList && <FontAwesomeIcon icon="check" color="green" style={{ marginLeft: '2px' }} /> }
                </RowLayout>
            </SMCExpansionPanel>
            {/* Gateway type tab */}
            <SMCExpansionPanel title={t('SMC_GATEWAY_VENDOR')}>
                <div style={{ paddingLeft: '8px' }}>
                    {/* Vendor */}
                    <SMCLabel>{t('SMP.TYPE')}:</SMCLabel>
                    <RowLayout>
                        <Select value={vendor} onChange={e => setVendor(e.target.value)} className={classes.selectRoot}>
                            <MenuItem classes={{ root: classes.vendorMenuItem, selected: classes.menuItemSelected }} value={GatewayFilterSchema.vendorValues.smallOfficeAppliance}>
                                Small Office Appliance
                            </MenuItem>
                            <MenuItem classes={{ root: classes.vendorMenuItem, selected: classes.menuItemSelected }} value={GatewayFilterSchema.vendorValues.SmallOfficeHa}>
                                Small Office HA
                            </MenuItem>
                        </Select>
                        <SMCIcon icon="clear" onClick={() => setVendor('')} />
                    </RowLayout>
                    {/* Gateway type */}
                    <SMCLabel>{t('SMC_GATEWAY_VENDOR')}:</SMCLabel>
                    <RowLayout>
                        <Select
                            multiple
                            value={gatewayTypesList}
                            onChange={gatewayTypesOnChange}
                            renderValue={selected => selected.join(', ')}
                            MenuProps={{
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                getcontentanchorel: null,
                                style: { maxHeight: 300 }
                            }}
                            className={classes.selectRoot}
                        >
                            {/* 600 Family */}
                            <SMCMenuItem title='true' id="600 Family" value="600 Family"><SMCCheckbox label="600 Family" checked={is600FamilySelected} /> </SMCMenuItem>
                            <SMCMenuItem value="600 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('600 Appliance')} label="600 Appliance" /></SMCMenuItem>
                            {/* 1200 Family */}
                            <SMCMenuItem title='true' id="1200 Family" value="1200 Family"><SMCCheckbox label="1200 Family" checked={is1200FamilySelected} /></SMCMenuItem>
                            <SMCMenuItem value="1200R Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1200R Appliance')} label="1200R Appliance" /></SMCMenuItem>
                            {/* 700/1400 Family */}
                            <SMCMenuItem title='true' id="700/1400 Family" value="700/1400 Family"><SMCCheckbox label="700/1400 Family" checked={is7001400FamilySelected}/></SMCMenuItem>
                            <SMCMenuItem value="730 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('730 Appliance')} label="730 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="750 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('750 Appliance')} label="750 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="790 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('790 Appliance')} label="790 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1430 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1430 Appliance')} label="1430 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1450 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1450 Appliance')} label="1450 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1470 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1470 Appliance')} label="1470 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1490 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1490 Appliance')} label="1490 Appliance" /></SMCMenuItem>
                            {/* 1500 Family */}
                            <SMCMenuItem title='true' id="1500 Family" value="1500 Family"><SMCCheckbox label="1500 Family" checked={is1500FamilySelected} /></SMCMenuItem>
                            <SMCMenuItem value="1500 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1500 Appliance')} label="1500 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1530 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1530 Appliance')} label="1530 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1550 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1550 Appliance')} label="1550 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1570 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1570 Appliance')} label="1570 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1590 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1590 Appliance')} label="1590 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1570R Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1570R Appliance')} label="1570R Appliance" /></SMCMenuItem>
                            {/* 1600/1800 Family */}
                            <SMCMenuItem title='true' id="1600/1800 Family" value="1600/1800 Family"><SMCCheckbox label="1600/1800 Family" checked={is16001800FamilySelected} /></SMCMenuItem>
                            <SMCMenuItem value="1600 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1600 Appliance')} label="1600 Appliance" /></SMCMenuItem>
                            <SMCMenuItem value="1800 Appliance"><SMCCheckbox checked={gatewayTypesList.includes('1800 Appliance')} label="1800 Appliance" /></SMCMenuItem>
                        </Select> 
                        <SMCIcon icon="clear" onClick={() => setGatewayTypesList([])} />
                    </RowLayout>
                </div>
            </SMCExpansionPanel>
            {/* Plan tab */}
            <SMCExpansionPanel title={t('SMC_GATEWAYS_PLAN')}>
                <div style={{ paddingLeft: '0px' }}>
                    <RowLayout>
                        <Select
                            value={plan}
                            onChange={e => setPlan(e.target.value)}
                            MenuProps={{
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                getcontentanchorel: null,
                                style: { maxHeight: 300 }
                            }}
                            className={classes.selectRoot}
                        >
                            {
                                planList && (
                                    planList.map((_plan) => (
                                        <MenuItem classes={{ root: classes.vendorMenuItem, selected: classes.menuItemSelected }} value={_plan} key={_plan}>
                                            {_plan}
                                        </MenuItem>
                                    ))
                                )
                            }
                        </Select>
                        <SMCIcon icon="clear" onClick={() => setPlan('')} />
                    </RowLayout>
                </div>
            </SMCExpansionPanel>
            {/* Firmware tab */}
            <SMCExpansionPanel title={t('SMC_GATEWAY_SOFTWAREVER')}>
                <div style={{ paddingLeft: '0px' }}>
                    <RowLayout>
                        <Select
                            multiple
                            value={firmwaresList}
                            onChange={e => setFirmwaresList(e.target.value)}
                            renderValue={selected => selected.join(', ')}
                            MenuProps={{
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                getcontentanchorel: null,
                                style: { maxHeight: 300 }
                            }}
                            className={classes.selectRoot}
                        >
                            {
                                gatewayUsedFirmwares && (
                                    gatewayUsedFirmwares.map((firmware) => (
                                        <MenuItem classes={{ root: classes.firmwareMenuItem, selected: classes.menuItemSelected }} value={firmware} key={firmware}>
                                            <SMCCheckbox checked={firmwaresList && firmwaresList.includes(firmware)} label={firmware} />
                                        </MenuItem>
                                    ))
                                )
                            }
                        </Select>
                        <SMCIcon icon="clear" onClick={() => setFirmwaresList([])} />
                    </RowLayout>
                </div>
            </SMCExpansionPanel>
            {/* First connection tab */}
            <SMCExpansionPanel title={t('SMC_GATEWAY_FIRSTCONN')}>
                <DateRangeChooser
                    fromDate={firstConnectionFromDate}
                    toDate={firstConnectionToDate}
                    setFromDate={setFirstConnectionFromDate}
                    setToDate={setFirstConnectionToDate}
                />
            </SMCExpansionPanel>
            {/* Last connection tab */}
            <SMCExpansionPanel title={t('SMC_GATEWAY_LASTCONNECT')}>
                <DateRangeChooser
                    fromDate={lastConnectionFromDate}
                    toDate={lastConnectionToDate}
                    setFromDate={setLastConnectionFromDate}
                    setToDate={setLastConnectionToDate}
                />
            </SMCExpansionPanel>
            {/* Creation tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_CREATETIME')}>
                <DateRangeChooser
                    fromDate={creationFromDate}
                    toDate={creationToDate}
                    setFromDate={setCreationFromDate}
                    setToDate={setCreationToDate}
                />
            </SMCExpansionPanel>
            {/* Creation tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_MODIFYTIME')}>
                <DateRangeChooser
                    fromDate={modificationFromDate}
                    toDate={modificationToDate}
                    setFromDate={setModificationFromDate}
                    setToDate={setModificationToDate}
                />
            </SMCExpansionPanel>
            {/* Popup pages */}
            <SetMacFileModal
                isOpen={isSetMacFolderModalOpen}
                onRequestClose={closeSetMacFolderModal}
                onFinish={(macAddressList) => {
                    setMacAddressList(macAddressList);
                    closeSetMacFolderModal();
                }}
            />
        </FilterLayout>
    );
}

const useStyles = makeStyles({
    selectRoot: {
        fontSize: '13px',
        width: 160
    },
    gatewayTypeMenuItem: {
        fontSize: '13px',
        fontWeight: 'normal',
        whiteSpace: 'pre',
        minHeight: '29px',
        padding: '2px 2px 2px 26px',
        display: 'flex',
    },
    gatewayTypeMenuTitle: {
        paddingLeft: '4px',
        fontSize: '13px',
        fontWeight: 'bold',
        whiteSpace: 'pre',
        minHeight: '29px',
        padding: '2px 2px 2px',
        display: 'flex',
    },
    vendorMenuItem: {
        fontSize: '13px',
        fontWeight: 'normal',
        whiteSpace: 'pre',
        minHeight: '29px',
        padding: '4px'
    },
    firmwareMenuItem: {
        fontSize: '13px',
        whiteSpace: 'pre',
        minHeight: '29px',
        padding: '2px 6px 2px 4px',
        display: 'flex',
    },
    menuItemSelected: {
        background: 'inherit !important'
    }
})

export default React.memo(GatewayFilterPane);